import * as React from 'react';
import {
  FC, useState, useCallback, MouseEvent, KeyboardEvent, useRef, useEffect
} from 'react';
import { style } from 'typestyle';
import { EventMap, IMedia } from '../../types';
import { useStore } from '../../services/store';
import ThumbnailPlayIcon from '../Icons/ThumbnailPlay';
import Mute from '../Icons/MuteUnmute/Mute';
import Unmute from '../Icons/MuteUnmute/Unmute';
import { Helpers } from '../../services/helpers';
import { onKeyDownEventCallback } from '../../utils/onKeyDown';
import useBorderRadius from '../../hooks/useBorderRadius';
import UserName from '../UserName';
import useLuminance from '../../hooks/useLuminance';
import { Packshot, getHeightPercentage } from '../Packshot';
import { ugcZIndex, pillZIndex, videoZIndex, activeSquareAspectRatio, activePortraitAspectRatio, activeStoriesAspectRatio, thumbnailTransitionDurationSec, topPillsMarginTopPx } from '../consts';
import AttributionLogo from '../Icons/AttributionLogo';

const ASPECT_RATIOS = {
  square: 100,
  portrait: 125,
  stories: 177.78,
} as const;

const ACTIVE_SLIDE_ASPECT_RATIOS = {
  square: activeSquareAspectRatio, 
  portrait: activePortraitAspectRatio, 
  stories: activeStoriesAspectRatio, 
} as const;

export const Thumbnail: FC<{ media: IMedia; position: number }> = ({ media, position }) => {
  const store = useStore();
  const [imageVisible, setImageVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const thumbnailRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const luminanceColor = useLuminance(media.image, 0.9, 0.1, 0.5, isLoaded, imgRef.current);
  const textColor = isLoaded ? luminanceColor : '#ffffff';

  const {
    thumbnail_corners: cornerType,
    thumbnail_shape: thumbnailShape = 'square',
    thumbnail_packshot: thumbnailPackshot,
    shop_this_look_display: shopThisLookDisplay,
    type: widgetLayout,
    thumbnail_video_autoplay_hover: videoAutoplayOnHover = true,
    show_attribution: showAttribution = true,
  } = store.data.settings;

  const thumbnailImage = media.thumbnail ?? media.image;
  const borderRadius = useBorderRadius(100, 100, cornerType);
  const productListMode = shopThisLookDisplay === 'product_list';
  const isProductListCarousel = productListMode && widgetLayout === 'carousel' && !store.isMobile;
  const allowProductListGrow = isProductListCarousel && !store.productListUniformMode;
  const isActiveSlide = store.currentPosition === media.postIndex;
  
  const shouldShowVideo = media.type === 'video' && 
                         media.video && 
                         isLoaded && 
                         ((productListMode || store.isMobile) && isActiveSlide || 
                         (!store.isMobile && videoAutoplayOnHover && isHovered));

  const shouldShowAttribution = showAttribution && (
    (isActiveSlide && productListMode) || 
    (isActiveSlide && store.isMobile && media.type === 'video') || 
    (isHovered && !store.isMobile)
  );

  const handleEvent = useCallback(
    (eventName: keyof EventMap, originalEvent?: Event) => {
      store.triggerEvent(eventName, { post: media, position, originalEvent }, store.data.id);
    },
    [media, position, store],
  );

  const handleImageLoad = useCallback(() => {
    setIsLoaded(true);
    handleEvent('thumbnailLoaded');
  }, [handleEvent]);
  
  const handleOpen = useCallback(
    (e: MouseEvent | KeyboardEvent) => {

      store.setStoreState((state) => ({ 
        ...state, 
        currentPosition: media.postIndex,
      }));

      if (store.data.settings.shop_this_look_display === 'product_list') {
        if (!media.products || media.products.length === 0) {
          return; // don't open product list if no products
        }
      }

      handleEvent('thumbnailClicked', e.nativeEvent);
      store.setStoreState((state) => ({ 
        ...state, 
        post: media,
        currentPosition: media.postIndex,
      }));
      Helpers.pushPostToHistory(media);
    },
    [handleEvent, media, store],
  );

  const handleImageError = useCallback(() => {
    handleEvent('thumbnailUnavailable');
    store.setStoreState((state) => ({
      ...state,
      data: {
        ...state.data,
        content: {
          ...state.data.content,
          medias: state.data.content.medias
            .filter((_, idx) => idx !== media.postIndex)
            .map((m, idx) => ({ ...m, postIndex: idx })),
        },
      },
    }));
  }, [handleEvent, media.postIndex, store]);

  // Sets up lazy loading logic based on layout type
  useEffect(() => {
    const isModalCarousel = !isProductListCarousel && widgetLayout === 'carousel';
    
    if (isModalCarousel) {
      // For modal carousel, only load images that are currently visible
      const isInVisibleRange = media.postIndex >= store.carouselSlidePosition && 
                        media.postIndex < store.carouselSlidePosition + store.carouselContentSize;

      if (isInVisibleRange && !isLoaded) {
        setImageVisible(true);
      }
    }

    // For other modes, use intersection observer that triggers when thumbnail enters/exits viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setImageVisible(true);
            observer.unobserve(entry.target); // stop since we don't need updates
          }
        });
      },
      {
        rootMargin: '0px', // no margin around viewport
        threshold: 0.1 // trigger when 10% visible
      }
    );

    if (thumbnailRef.current) {
      observer.observe(thumbnailRef.current);
    }

    return () => {
      if (thumbnailRef.current) {
        observer.unobserve(thumbnailRef.current);
      }
    };
  }, [shopThisLookDisplay, widgetLayout, store.carouselSlidePosition, store.carouselContentSize, media.postIndex, isLoaded]);

  const imageClassName = isLoaded 
    ? (isActiveSlide ? activeImageClass : inactiveImageClass)
    : '';

  // Get the aspect ratio based on active state and mode
  const getAspectRatio = () => {
    if (allowProductListGrow && isActiveSlide) {
      return ACTIVE_SLIDE_ASPECT_RATIOS[thumbnailShape];
    }
    return ASPECT_RATIOS[thumbnailShape];
  };

  // Calculate packshot height percentage, adjusting for active slides
  const getAdjustedPackshotHeight = () => {
    const baseHeight = getHeightPercentage(thumbnailShape, store.isMobile);
    
    if (allowProductListGrow && isActiveSlide) {
      // Calculate ratio between standard and active aspect ratios
      const aspectRatio = ASPECT_RATIOS[thumbnailShape];
      const activeAspectRatio = ACTIVE_SLIDE_ASPECT_RATIOS[thumbnailShape];
      const ratio = aspectRatio / activeAspectRatio;
      
      // Apply this ratio to maintain consistent visual height
      return baseHeight * ratio;
    }
    
    return baseHeight;
  };

  let userNameClasses = `${userNameClass(textColor)} ${store.config.classPrefix}-thumbnail-pill`;
  if (!store.isMobile) {
    userNameClasses += ` ${userNameOpacityTransitionClass}`;
  }

  const handleMouseEnter = useCallback(() => {
    handleEvent('thumbnailHover');
    setIsHovered(true);
  }, [handleEvent]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // Control video playback based on hover state
  useEffect(() => {
    if (!store.isMobile && videoAutoplayOnHover && videoRef.current) {
      if (isHovered) {
        videoRef.current.play().catch(() => {
          // Silent catch for browsers that block autoplay
        });
      } else if (!isActiveSlide || !productListMode) {
        videoRef.current.pause();
      }
    }
  }, [isHovered, videoAutoplayOnHover, store.isMobile, isActiveSlide, productListMode]);

  return (
    <div
      ref={thumbnailRef}
      data-url={media.post}
      data-position={position}
      className={`${store.config.classPrefix}-thumbnail ${thumbnailGrowTransitionClass}`}
      style={{
        ...styles.thumbnail(),
        paddingTop: `${getAspectRatio()}%`,
      }}
      onClick={handleOpen}
      onKeyDown={(e) => onKeyDownEventCallback(e, handleOpen)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
      role='link'
      aria-label={`open the preview of the ${media.source} content created by ${media.username}`}
    >
      <div className={containerClass}>
        <div 
          className={`${imageClassName} ${
            isActiveSlide 
              ? `${store.config.classPrefix}-thumbnail-content-active` 
              : `${store.config.classPrefix}-thumbnail-content-inactive`
          }`}
          style={{
            ...styles.image(borderRadius),
            ...(isLoaded && { backgroundImage: `url("${thumbnailImage}")` }),
          }}
        >
          {media.type === 'video' && (!shouldShowVideo) && <ThumbnailPlayIcon style={{ flex: 1 }} />}
          {shouldShowVideo ? (
            <>
              <video 
                ref={videoRef}
                autoPlay={isActiveSlide || (videoAutoplayOnHover && isHovered)}
                loop
                muted={isMuted}
                playsInline
                className={`${videoClass} ${store.config.classPrefix}-thumbnail-video`}
              >
                <source src={media.video} type='video/mp4' />
              </video>
              
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMuted(!isMuted);
                }}
                className={`${muteButtonClass(textColor)} ${store.config.classPrefix}-thumbnail-pill`}
              >
                <span style={styles.iconContainer}>
                  {isMuted ? 
                    <Mute color={textColor} size={20} /> : 
                    <Unmute color={textColor} size={20} />
                  }
                </span>
              </button>
            </>
          ) : null}
          {media.username && isLoaded ? (
            <div data-username={media.username} className={userNameClasses}>
              <UserName
                color={textColor}
                username={media.username}
                source={media.source}
                postUrl={media.post}
                hideUsername={store.isMobile && store.data.settings.type !== 'carousel'}
              />
            </div>
          ) : null}

          {shouldShowAttribution && (
            <div className={watermarkContainerClass}>
              <AttributionLogo color="rgba(255, 255, 255, 0.9)"  size={store.isMobile ? 12 : 20} />
            </div>
          )}

          {thumbnailPackshot && media.products && media.products.length > 0 && isLoaded ? (
            <Packshot 
              products={media.products} 
              shape={thumbnailShape} 
              cornerType={cornerType}
              heightPercentage={getAdjustedPackshotHeight()} 
              hidden={isActiveSlide && (productListMode || store.isMobile) || isHovered}
            />
          ) : null}
        </div>
      </div>

      {imageVisible ? (
        <img
          ref={imgRef}
          src={thumbnailImage}
          alt={`${media.source} content created by ${media.username}`}
          crossOrigin="anonymous"
          onError={handleImageError}
          onLoad={handleImageLoad}
          style={{ display: 'none' }}
        />
      ) : null}
    </div>
  );
};

const styles = {
  thumbnail: () => ({
    width: '100%',
    position: 'relative' as const,
    cursor: 'pointer',
  }),

  image: (cornerRadius: string) => ({
    position: 'relative' as const,
    flex: 1,
    margin: 4,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: '#282828',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    overflow: 'hidden',
    borderRadius: cornerRadius,
  }),


  iconContainer: {
    display: 'flex',
    alignItems: 'center',
  },
} as const;

const containerClass = style({
  position: 'absolute',
  height: '100%',
  width: '100%',
  top: 0,
  left: 0,
  display: 'flex',
  $nest: {
    '&:hover .username': {
      opacity: 1,
    },
  },
});

const activeImageClass = style({
  zIndex: ugcZIndex,
  transition: `transform ${thumbnailTransitionDurationSec}s ease`,
});

const inactiveImageClass = style({
  zIndex: 1,
  transition: `transform ${thumbnailTransitionDurationSec}s ease`,
});

const videoClass = style({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: videoZIndex,
});

const userNameClass = (textColor: string) => `${style({
  position: 'absolute',
  zIndex: pillZIndex,
  top: topPillsMarginTopPx,
  right: 5,
  textDecoration: 'none',
  color: textColor
})} username`; // we use this to target the username in the thumbnail for hover styling

const userNameOpacityTransitionClass = style({
  opacity: 0,
  transition: 'opacity 0.2s ease',
});

const muteButtonClass = (textColor: string) => style({
  position: 'absolute',
  top: `${topPillsMarginTopPx}px`,
  left: '10px',
  zIndex: pillZIndex,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(3px)',
  '-webkit-backdrop-filter': 'blur(3px)',
  padding: '6px 12px',
  borderRadius: '99px',
  color: textColor,
  border: 'none',
  cursor: 'pointer',
  fontSize: '12px',
  fontFamily: "'Fustat', sans-serif",
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
});

const thumbnailGrowTransitionClass = style({
  transition: 'padding-top 0.4s ease',
});

const watermarkContainerClass = style({
  position: 'absolute',
  bottom: 3,
  left: 5,
  zIndex: pillZIndex,
  opacity: 0.6, 
  cursor: 'pointer',
});

export default Thumbnail;
